import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IntegrationsService } from 'src/providers/IntegrationsProvider/Integrations.service';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useSyncConversationsContext } from 'src/providers/SyncConversationsProvider/SyncConversations.context';
import { eIntegrationType } from 'src/providers/IntegrationsProvider/Integrations.enums';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';

export default function useSyncConversationsSelect() {
  const {
    conversations,
    setConversations,
    setHandles,
    setTotalConversations,
    selectedIntegration,
    selectedPagUUid,
  } = useSyncConversationsContext();
  const [loading, setLoading] = useState(true);
  const [progressBar, setProgressBar] = useState(0);
  const { currentWorkspaceId } = useWorkspaceContext();
  const { addAlert } = useToastContext();
  const { t } = useTranslation('dashboard/dashboard');

  useEffect(() => {
    let cancelled = false;
    setConversations([]);
    setHandles([]);
    setTotalConversations(0);
    setLoading(true);
    setProgressBar(0);

    const loadConversations = async () => {
      try {
        const isWhatsapp =
          selectedIntegration === eIntegrationType.WHATSAPP_WEB;
        if (isWhatsapp ? !currentWorkspaceId : !selectedPagUUid) return;
        const response = isWhatsapp
          ? await IntegrationsService.getConversationsFromChatDaddy({
              workspaceId: currentWorkspaceId!,
            })
          : await IntegrationsService.getMetaConversations({
              pageUuid: selectedPagUUid,
              type: selectedIntegration,
            });
        if (cancelled) return;
        const next = (response.data ?? []).map(
          ({ handle, name, updatedAt }) => ({ handle, name, updatedAt }),
        );
        setConversations(next);
        setHandles(next.map(({ handle }) => handle).reverse());
        setTotalConversations(next.length);
      } catch {
        if (!cancelled)
          addAlert({
            type: eAlertType.ERROR,
            message: t('components.modals.SyncConversations.select.loadError'),
          });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (selectedIntegration !== eIntegrationType.WHATSAPP_WEB) {
      void loadConversations();
      return () => {
        cancelled = true;
      };
    }

    // Allow the on-link history to arrive, then fetch once. Keep the request
    // outside the state updater, which React may evaluate more than once.
    let progress = 0;
    const interval = setInterval(() => {
      progress += 1;
      setProgressBar(progress);
      if (progress >= 100) {
        clearInterval(interval);
        void loadConversations();
      }
    }, 200);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentWorkspaceId, selectedIntegration, selectedPagUUid]);

  return { conversations, loading, progressBar, metaError: undefined };
}
